import {
  addDays,
  addMilliseconds,
  addMonths,
  addWeeks,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  type Locale,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from "date-fns";

import {
  type CalendarEvent,
  type CalendarRange,
  type CalendarView,
} from "./types";

// =============================================================================
// Pure date helpers of the calendar: grids, navigation and labels. No React.
// =============================================================================

export type WeekStartsOn = 0 | 1;

/** "yyyy-MM-dd": cheap key to index events by day */
export function getDayKey(date: Date): string {
  return format(date, "yyyy-MM-dd");
}

/** Wall-clock time `minutes` after the midnight of `day` (DST-safe) */
export function atMinutes(day: Date, minutes: number): Date {
  return new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, minutes);
}

/** Last instant covered by the event (`end` is exclusive) */
export function getInclusiveEnd(event: CalendarEvent): Date {
  return event.end.getTime() > event.start.getTime()
    ? addMilliseconds(event.end, -1)
    : event.start;
}

// =============================================================================
// Hidden weekdays
// =============================================================================

/**
 * Valid weekday numbers (0 … 6) to hide. Hiding the seven days would leave
 * empty views: that configuration is ignored.
 */
export function sanitizeHiddenWeekdays(
  hiddenWeekdays: readonly number[]
): Set<number> {
  const set = new Set(
    hiddenWeekdays.filter(
      (day) => Number.isInteger(day) && day >= 0 && day <= 6
    )
  );
  return set.size >= 7 ? new Set() : set;
}

/** Closest visible day from `date` (inclusive), walking by `step` */
export function skipHiddenDays(
  date: Date,
  step: 1 | -1,
  hiddenWeekdays: ReadonlySet<number>
): Date {
  let next = date;
  while (hiddenWeekdays.has(next.getDay())) next = addDays(next, step);
  return next;
}

// =============================================================================
// Grids
// =============================================================================

/** The days of the week containing `date`, without the hidden weekdays */
export function getVisibleWeekDays(
  date: Date,
  weekStartsOn: WeekStartsOn,
  hiddenWeekdays: ReadonlySet<number>
): Date[] {
  const start = startOfWeek(date, { weekStartsOn });
  return Array.from({ length: 7 }, (_, i) => addDays(start, i)).filter(
    (day) => !hiddenWeekdays.has(day.getDay())
  );
}

/**
 * Full weeks covering the month of `date` (4 to 6 rows), adjacent-month days
 * included, hidden weekdays removed from every row.
 */
export function getMonthGrid(
  date: Date,
  weekStartsOn: WeekStartsOn,
  hiddenWeekdays: ReadonlySet<number>
): Date[][] {
  const days = eachDayOfInterval({
    start: startOfWeek(startOfMonth(date), { weekStartsOn }),
    end: endOfWeek(endOfMonth(date), { weekStartsOn }),
  });
  const weeks: Date[][] = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(
      days.slice(i, i + 7).filter((day) => !hiddenWeekdays.has(day.getDay()))
    );
  }
  return weeks;
}

// =============================================================================
// Navigation
// =============================================================================

/** Moves `date` by `amount` units of the view (date-fns, DST-safe) */
export function addToView(
  date: Date,
  view: CalendarView,
  amount: number
): Date {
  return view === "week" ? addWeeks(date, amount) : addMonths(date, amount);
}

/**
 * Exact span displayed by the view (`end` exclusive). The month view includes
 * the leading and trailing days of the adjacent months.
 */
export function getVisibleRange(
  view: CalendarView,
  date: Date,
  weekStartsOn: WeekStartsOn
): CalendarRange {
  if (view === "week") {
    const start = startOfWeek(date, { weekStartsOn });
    return { start, end: addDays(start, 7) };
  }
  const start = startOfWeek(startOfMonth(date), { weekStartsOn });
  const lastDay = startOfDay(endOfWeek(endOfMonth(date), { weekStartsOn }));
  return { start, end: addDays(lastDay, 1) };
}

// =============================================================================
// Labels
// =============================================================================

/** Month and weekday names are lowercase in some locales (French…) */
export function capitalizeFirst(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/** Localized short time: "10:30" (fr), "10:30 AM" (en-US) */
export function formatTime(date: Date, locale: Locale): string {
  return format(date, "p", { locale });
}

/** Abbreviated weekday without the trailing dot: "Mon", "Lun" */
export function formatWeekday(date: Date, locale: Locale): string {
  return capitalizeFirst(format(date, "EEE", { locale }).replace(".", ""));
}

/** Full day label: "Tuesday, October 14" (en-US), "Mardi 14 octobre" (fr) */
export function formatDayLabel(
  date: Date,
  locale: Locale,
  withYear = false
): string {
  return capitalizeFirst(
    new Intl.DateTimeFormat(locale.code, {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: withYear ? "numeric" : undefined,
    }).format(date)
  );
}

/**
 * When an event happens, written the way the locale writes a range: the days
 * of an all-day event, the date and hours of a timed one ("Tuesday, October
 * 14, 10:00 – 11:30 AM").
 */
export function formatEventWhen(event: CalendarEvent, locale: Locale): string {
  const formatter = new Intl.DateTimeFormat(locale.code, {
    weekday: "long",
    day: "numeric",
    month: "long",
    ...(event.allDay ? {} : { hour: "numeric", minute: "2-digit" }),
  });
  return capitalizeFirst(
    event.allDay
      ? formatter.formatRange(event.start, getInclusiveEnd(event))
      : formatter.formatRange(event.start, event.end)
  );
}

/**
 * Toolbar title: the month ("July 2026") or the visible days of the week,
 * written the way the locale writes a range ("July 13 – 19, 2026",
 * "29 juin – 5 juillet 2026").
 */
export function formatPeriodTitle(
  view: CalendarView,
  date: Date,
  locale: Locale,
  weekStartsOn: WeekStartsOn,
  hiddenWeekdays: ReadonlySet<number>
): string {
  if (view === "month") {
    return capitalizeFirst(format(date, "LLLL yyyy", { locale }));
  }
  const days = getVisibleWeekDays(date, weekStartsOn, hiddenWeekdays);
  const formatter = new Intl.DateTimeFormat(locale.code, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  return capitalizeFirst(formatter.formatRange(days[0], days[days.length - 1]));
}
